import { useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtimePostgresChanges } from './useRealtimePostgresChanges';

/** Die Schranke fordert genau die zwei Felder, die dieser Hook selbst anfasst.
 *  `total_products: number` stand hier bis zum Typecheck-Gate und war der Grund,
 *  warum `useAsyncJob<ProductSyncJob>` nicht kompilierte: die Tabelle
 *  `product_sync_jobs` HAT diese Spalte nicht (sie fuehrt `total_fetched` und
 *  `total_pages`), und der Hook las das Feld nie — es kam im ganzen Modul nur in
 *  der Schranke selbst vor. Die uebrigen Aufrufer lesen es weiterhin ueber ihr
 *  eigenes `T` (z. B. `useAsyncJob<UploadJob>`). */
export interface AsyncJobBase {
  id: string;
  status: string;
}

/** Die Job-Tabellen, die dieser Hook bedient — vorher `string` plus ein
 *  Any-Cast am `.from()`, und genau dieser Cast liess `.select('*')` zu einem
 *  `SelectQueryError`-Verbund werden, der sich nicht mehr auf `T` casten liess.
 *  Bewusst diese sechs Namen statt `keyof Database['public']['Tables']`: ueber
 *  der vollen Tabellenmenge laeuft die Typaufloesung von postgrest-js in
 *  `TS2589 Type instantiation is excessively deep` (gemessen, nicht vermutet).
 *  Ein Tippfehler im Tabellennamen ist damit trotzdem ein Compile-Fehler statt
 *  einer stillen 404 zur Laufzeit. */
export type JobTableName =
  | 'optimization_jobs'
  | 'upload_jobs'
  | 'product_sync_jobs'
  | 'score_calculation_jobs'
  | 'attribute_extraction_jobs'
  | 'excel_export_jobs';

interface UseAsyncJobOptions {
  tableName: JobTableName;
  channelName: string;
  queryKeyPrefix: string;
  feedId: string | null;
  /** Keep polling even when query returns null (e.g. for optimistic local state) */
  forcePolling?: boolean;
}

/** Antwort von `cancel_async_job` (Migration 20260927100000). `cancelled: false`
 *  heisst: der Job war schon fertig, fehlgeschlagen oder abgebrochen, es wurde
 *  nichts geaendert. */
export interface CancelJobResult {
  cancelled: boolean;
  status: string | null;
  deleted_messages: number;
}

export function useAsyncJob<T extends AsyncJobBase>({
  tableName,
  channelName,
  queryKeyPrefix,
  feedId,
  forcePolling = false,
}: UseAsyncJobOptions) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => [queryKeyPrefix, feedId, user?.id],
    [queryKeyPrefix, feedId, user?.id],
  );

  // Poll for active job
  const { data: activeJob } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!feedId || !user?.id) return null;
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('user_id', user.id)
        .eq('feed_id', feedId)
        .in('status', ['queued', 'processing'])
        .order('created_at', { ascending: false })
        .limit(1)
        // `maybeSingle<T>()` statt eines Casts auf der Rueckgabe: postgrest-js
        // laesst die Zeilenform hier ausdruecklich setzen
        // (`maybeSingle<ResultOne>()`), `data` ist damit `T | null` und der
        // fruehere `data as T | null` entfaellt ganz.
        .maybeSingle<T>();
      // Fehlerkanal NICHT verwerfen (Konvergenz 4, Befund 3): postgrest-js
      // liefert 401 (ablaufende Session) und 5xx als { data: null, error } —
      // frueher wurde daraus ein ERFOLGREICHES "kein Job", React Query
      // stoppte das Polling endgueltig und ein offenes Fortschritts-Modal
      // fror bei gruenem Live-Puls ein. Werfen laesst React Query retrien
      // und erhaelt die letzten bekannten Daten (query.state.data), sodass
      // das refetchInterval weiterlaeuft.
      if (error) throw error;
      return data;
    },
    enabled: !!feedId && !!user?.id,
    refetchInterval: (query) => {
      return (query.state.data || forcePolling) ? 3000 : false;
    },
  });

  // Realtime subscription for job status changes.
  //
  // Caveat, deliberately written down rather than assumed away: this only delivers for
  // tables that are members of the publication supabase_realtime. As of 2026-07-26 that
  // covers product_sync_jobs, score_calculation_jobs, attribute_extraction_jobs and
  // excel_export_jobs; upload_jobs is added by migration
  // 20260726220000_realtime_missing_job_tables.sql.
  //
  // optimization_jobs is deliberately NOT published and this hook does NOT get realtime
  // for it — progress there comes purely from the 3s refetchInterval below. The reason
  // is volume: optimization_jobs carries the ~1.5s heartbeat_at write, measured at
  // 308,482 lifetime UPDATEs across only 113 jobs. Publishing that would push every
  // heartbeat through WAL decoding and out to every subscribed tab, and the cost has
  // never been measured. Do not "fix" this by adding the table to the publication
  // without measuring first.
  useRealtimePostgresChanges(
    feedId && user?.id ? channelName : null,
    user?.id ? { event: '*', table: tableName, filter: `user_id=eq.${user.id}` } : null,
    () => {
      queryClient.invalidateQueries({ queryKey });
    },
  );

  /**
   * Bricht genau diesen Job ab (UX-Audit K01). Der Server prueft, dass der Job
   * dem Aufrufer gehoert, setzt queued/processing auf cancelled und loescht NUR
   * die Queue-Nachrichten dieses Jobs. Frueher leerte `pgmq_purge` hier die
   * ganze Queue aller Kunden. Jeder RPC-Fehler wird geworfen, damit der Aufrufer
   * nicht "abgebrochen" meldet, obwohl nichts passiert ist.
   */
  const cancelJob = useCallback(async (jobId: string): Promise<CancelJobResult> => {
    try {
      const { data, error } = await supabase.rpc('cancel_async_job', {
        p_job_table: tableName,
        p_job_id: jobId,
      });
      if (error) throw error;
      return data as unknown as CancelJobResult;
    } finally {
      queryClient.invalidateQueries({ queryKey });
    }
  }, [tableName, queryClient, queryKey]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return { activeJob, cancelJob, invalidate };
}
